import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Sparkles } from "lucide-react";
import { PLAN_TIER_LABELS } from "@shared/modular-pricing";
import { formatUsd, type BillBreakdown, type PlanSelection } from "./plan-selection";

/**
 * The live itemized bill. On-PadSplit and off-PadSplit room charges render as
 * separate lines (the off-PS line carries the Member Portals surcharge as its
 * own row), the Owner Portals line shows billed vs. waived counts, and the
 * volume discount comes off the ENTIRE subtotal. When à-la-carte Essentials
 * costs more than Pro, the Pro nudge names the exact monthly savings.
 */
export function BillPanel({
  selection,
  quote,
  ctaLabel,
  ctaPending,
  onSubmit,
  onSwitchToPro,
  footnote,
}: {
  selection: PlanSelection;
  quote: BillBreakdown;
  ctaLabel: string;
  ctaPending?: boolean;
  onSubmit: () => void;
  onSwitchToPro: () => void;
  footnote?: React.ReactNode;
}) {
  const showProNudge =
    selection.planTier === "essentials" && (quote.proSavingsCents ?? 0) > 0;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-lg p-6 space-y-4" data-testid="panel-live-bill">
      <h3 className="text-lg font-bold text-gray-900">Your monthly bill</h3>

      <div className="space-y-2 text-sm" data-testid="list-bill-lines">
        {quote.lines.length === 0 && (
          <p className="text-gray-500">Add rooms to see your bill.</p>
        )}
        {quote.lines.map((line) => (
          <div key={line.key} className="flex justify-between gap-3" data-testid={`line-${line.key}`}>
            <span className="text-gray-600 min-w-0">
              {line.label}
              {line.quantity > 1 && (
                <span className="text-gray-400"> × {line.quantity.toLocaleString("en-US")}</span>
              )}
            </span>
            <span className="text-gray-900 font-medium shrink-0">{formatUsd(line.amountCents)}</span>
          </div>
        ))}
        {selection.accountKind === "property_manager" && quote.waivedOwnerCount > 0 && (
          <div className="flex justify-between gap-3 text-emerald-700" data-testid="line-owner-waiver">
            <span>Owner Portals waived ({quote.waivedOwnerCount} owners with packages)</span>
            <span className="font-medium">$0</span>
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotal</span>
          <span className="text-gray-900 font-medium" data-testid="text-bill-subtotal">
            {formatUsd(quote.subtotalCents)}
          </span>
        </div>
        {quote.volumeDiscountPercent > 0 && (
          <div className="flex justify-between text-emerald-700" data-testid="line-volume-discount">
            <span>Volume discount ({quote.volumeDiscountPercent}% · {quote.combinedRooms.toLocaleString("en-US")} rooms)</span>
            <span className="font-medium">−{formatUsd(quote.volumeDiscountCents)}</span>
          </div>
        )}
        <div className="flex justify-between items-baseline pt-1">
          <span className="text-base font-bold text-gray-900">Total</span>
          <span className="text-3xl font-extrabold text-gray-900" data-testid="text-bill-total">
            {formatUsd(quote.totalCents)}
            <span className="text-sm font-normal text-gray-500">/mo</span>
          </span>
        </div>
        {quote.combinedRooms > 0 && (
          <p className="text-xs text-gray-500" data-testid="text-effective-per-room">
            Effective {formatUsd(quote.effectivePerRoomOnCents)}/room on PadSplit
            {selection.roomsOffPadsplit > 0 && <> · {formatUsd(quote.effectivePerRoomOffCents)}/room off PadSplit</>}
          </p>
        )}
      </div>

      {showProNudge && (
        <button
          type="button"
          onClick={onSwitchToPro}
          className="w-full rounded-xl border border-brand/30 bg-brand/5 p-3 text-left hover:bg-brand/10 transition-colors"
          data-testid="button-pro-nudge"
        >
          <div className="flex items-center gap-2 text-brand font-semibold text-sm">
            <Sparkles size={16} className="shrink-0" />
            Switch to {PLAN_TIER_LABELS[selection.accountKind].pro} and save {formatUsd(quote.proSavingsCents ?? 0)}/mo
          </div>
          <p className="text-xs text-gray-600 mt-0.5">
            You get every module plus Devices for less than your current selection.
          </p>
        </button>
      )}

      <Button
        className="w-full bg-brand hover:bg-brand/90 text-white font-semibold shadow-lg shadow-brand/20"
        size="lg"
        onClick={onSubmit}
        disabled={ctaPending}
        data-testid="button-calculator-cta"
      >
        {ctaPending ? "One moment…" : ctaLabel} {!ctaPending && <ArrowRight size={16} className="ml-2" />}
      </Button>

      {footnote}
    </div>
  );
}
